import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../../shared/api/client";
import type { AdminUser, Permission, Role } from "../../shared/api/types";
import { useI18n } from "../../shared/i18n";
import { Icon } from "../../shared/ui/icons";
import { Select } from "../../shared/ui/Select";
import { AccessGrid, SYSTEMS, drawn, followers, manifest } from "./AccessGrid";

interface Override {
  key: string;
  effect: "grant" | "revoke";
  reason: string;
}

interface Access {
  roles: string[];
  effective: string[];
  overrides: Override[];
}


export function UserEditor({
  user,
  roles,
  permissions,
  implications,
  onSaved,
  onClose,
}: {
  user: AdminUser;
  roles: Role[];
  permissions: Permission[];
  implications: Record<string, string[]>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();

  const [fullName, setFullName] = useState(user.full_name);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(user.roles[0] ?? "");
  const [isActive, setIsActive] = useState(user.is_active);

  const [access, setAccess] = useState<Access | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  /** Returns what it loaded, so a save can compare it with what it sent. */
  const loadAccess = useCallback(async (): Promise<Access | null> => {
    try {
      const loaded = await api.get<Access>(`/users/${user.id}/access`);
      setAccess(loaded);
      return loaded;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("users.loadFailed"));
      return null;
    }
  }, [user.id, t]);

  useEffect(() => {
    void loadAccess();
  }, [loadAccess]);

  /** THE ACCESS STATE, owned here rather than inside the grid.
   *
   *  It used to live in AccessGrid and reset itself from `access` whenever that prop
   *  changed. Saving the identity form refetched access, which replaced it -- so every tick
   *  an administrator had made and not yet saved was silently discarded, and the screen
   *  then showed a green "Saved". Keyed on the user id, so it seeds once per person and a
   *  refetch for the SAME person no longer throws away what they are editing. The one
   *  other reseed is a save that landed in full, when nothing is left unsaved to lose. */
  const [held, setHeld] = useState<Set<string>>(new Set());
  useEffect(() => {
    setHeld(new Set(access?.effective ?? []));
  }, [user.id, access === null]);

  const selectedRole = roles.find((r) => r.key === role);

  /** What the chosen role gives, CLOSED over the implication graph.
   *
   *  Closing it is not a nicety. `/roles` returns each role's DIRECT rows and the server
   *  stores a per-user row only where the person differs from their role, so diffing
   *  against the direct rows would make every implied key look like a difference -- and
   *  saving an unchanged administrator would convert all of their implied access into
   *  explicit grants that then outlive the role they came from. */
  const close = useCallback(
    (keys: Iterable<string>) => {
      const closed = new Set(keys);
      for (let moved = true; moved; ) {
        moved = false;
        for (const key of [...closed]) {
          for (const implied of implications[key] ?? []) {
            if (!closed.has(implied)) {
              closed.add(implied);
              moved = true;
            }
          }
        }
      }
      return closed;
    },
    [implications],
  );
  const fromRole = useMemo(
    () => close(selectedRole?.permissions ?? []),
    [selectedRole, close],
  );

  /** What the grid starts from for a role: what the person WILL hold on it -- the role's
   *  closure with their own grants and revokes applied, the server's rule -- or, on the
   *  role they already have, what they hold now. */
  const baseline = useCallback(
    (roleKey: string): Set<string> => {
      if (!access) return new Set();
      if (roleKey === (user.roles[0] ?? "")) return new Set(access.effective);
      const revoked = new Set(
        access.overrides.filter((o) => o.effect === "revoke").map((o) => o.key),
      );
      const direct = roles.find((r) => r.key === roleKey)?.permissions ?? [];
      const granted = access.overrides.filter((o) => o.effect === "grant").map((o) => o.key);
      return close([...direct, ...granted].filter((k) => !revoked.has(k)));
    },
    [access, roles, user.roles, close],
  );
  const startsFrom = useMemo(() => baseline(role), [baseline, role]);

  /** A NEW ROLE MOVES THE GRID. A system nobody has touched shows what the person will
   *  hold on the new role, so what is on screen when Save is pressed is what they get.
   *  A system somebody has touched keeps its ticks, and its followers become the new
   *  role's: they are the role's to give, not the old role's to carry over. Before
   *  this, the grid kept the OLD role's ticks after a role change, and a save that also
   *  edited a system turned them into per-user grants. */
  function changeRole(next: string) {
    const before = baseline(role);
    const after = baseline(next);
    const nextRole = close(roles.find((r) => r.key === next)?.permissions ?? []);
    setHeld((prev) => {
      const out = new Set(prev);
      for (const system of SYSTEMS) {
        const touched = drawn(system).some((k) => prev.has(k) !== before.has(k));
        if (!touched) {
          for (const k of manifest(system)) {
            if (after.has(k)) out.add(k);
            else out.delete(k);
          }
          continue;
        }
        for (const [, view, , follows = []] of system.rows) {
          for (const k of follows) {
            if (out.has(view) && nextRole.has(k)) out.add(k);
            else out.delete(k);
          }
        }
      }
      return out;
    });
    setRole(next);
  }

  /** Which systems have unsaved ticks, named so the button can say what is pending.
   *  Measured on the boxes drawn, against what the chosen role starts from. */
  const pendingSystems = SYSTEMS.filter((system) =>
    drawn(system).some((k) => held.has(k) !== startsFrom.has(k)),
  );
  const profileChanged =
    fullName !== user.full_name || email !== user.email ||
    isActive !== user.is_active || role !== (user.roles[0] ?? "") || password !== "";
  const dirty = profileChanged || pendingSystems.length > 0;

  /** What the chosen role grants, split by whether it can be adjusted per person. */
  const roleGrants = useMemo(() => {
    const kind = new Map(permissions.map((p) => [p.key, p.kind]));
    const granted = selectedRole?.permissions ?? [];
    return {
      features: granted.filter((k) => kind.get(k) === "feature").sort(),
    };
  }, [selectedRole, permissions]);


  // `setState` lived here: one PUT /users/{id}/access per permission, the writer behind the
  // Revoked / Default / Granted list. The list is gone and the grid saves a whole module in
  // one transaction through /access/grid, so nothing calls it. The ENDPOINT stays — it is
  // what a migration and the API tests use to set a single override, and it is the only way
  // to record an explicit revoke that the grid expresses implicitly.
  /** ONE Save for the screen, because it is one decision.
   *
   *  There were three: the identity form's, and one per system in the access grid. An
   *  administrator who changed a role and some sections had to press all three, in an order
   *  nothing stated, and pressing the wrong one first threw the other work away.
   *
   *  IDENTITY FIRST, and that ordering is load-bearing. The access diff is computed against
   *  the closure of the role SELECTED IN THE FORM, so saving sections before the role lands
   *  would send grants and revocations measured against a role the server does not have --
   *  and a revoke computed that way removes access the person should keep.
   *
   *  It is still several requests: the profile is `PUT /users/{id}` and each system is
   *  `PUT /users/{id}/access/grid`, one transaction per system. So a failure part way is
   *  possible, and the honest handling is to STOP, leave the systems that did not land
   *  visibly unsaved, and name what did not save. It never reports success it did not have.
   *  The better shape is for the server to take the whole desired state in one transaction;
   *  that is a bigger change than the button, and it is recorded in DECISIONS.md.
   */
  async function saveAll(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setWarning(null);
    const stillPending = [...pendingSystems];
    // The ticks this save sends. Compared, afterwards, with what the server then holds.
    const sent = held;
    try {
      if (profileChanged) {
        await api.put(`/users/${user.id}`, {
          full_name: fullName,
          email,
          is_active: isActive,
          role,
          new_password: password || null,
        });
        setPassword("");
      }

      for (const system of pendingSystems) {
        const keys = manifest(system);
        // From the manifest, never from `effective`: a prefix filter over the effective
        // set sweeps in keys this screen does not draw -- `analysis:admin` among them --
        // and would write an explicit grant of one that outlives a later demotion.
        const revoked = keys.filter((k) => !held.has(k) && fromRole.has(k));
        // Granted is measured against what the role STILL gives once those revokes land,
        // not against its whole closure. A key the role gives only by implication -- a
        // Roles-page role holding `grooming:assign` alone reaches `grooming:read` that
        // way -- goes when its implier is revoked, and a box left ticked on it would
        // come back unticked after the save.
        const stays = close(
          (selectedRole?.permissions ?? []).filter((k) => !revoked.includes(k)),
        );
        // Never a follower: those come from the role (see `followers`).
        const roleOnly = followers(system);
        const granted = keys.filter((k) => held.has(k) && !stays.has(k) && !roleOnly.has(k));
        await api.put(`/users/${user.id}/access/grid`, {
          module: system.module,
          rendered: keys,
          granted,
          revoked,
          reason: "",
        });
        stillPending.shift();
      }

      // Everything landed, so the ticks start again from what the server now holds.
      // Without this the editor kept comparing the ticks it SEEDED with -- before a role
      // change, before an implication put a box back -- against the refetched set, and
      // said Saved and Unsaved together, with Save still live over a stale state. Only
      // if nobody ticked anything while the requests were out: those ticks are unsaved,
      // and replacing them is the data loss this editor already had once.
      const fresh = await loadAccess();
      if (fresh) {
        // Only the systems this save wrote. Any other one moved because the ROLE changed,
        // which is the point of changing it, not a surprise to report.
        const differs = pendingSystems.filter((system) =>
          drawn(system).some((k) => sent.has(k) !== fresh.effective.includes(k)),
        );
        setHeld((current) => (current === sent ? new Set(fresh.effective) : current));
        if (differs.length > 0) {
          // Not "Saved": the boxes will not look the way they were left. Name where.
          setWarning(
            t("users.savedDiffers", { systems: differs.map((x) => x.title).join(", ") }),
          );
        } else {
          setNote(t("users.saved"));
          window.setTimeout(() => setNote(null), 2500);
        }
      }
      onSaved();
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : t("users.updateFailed");
      // Name what did NOT save. "Something went wrong" over a half-applied change leaves an
      // administrator guessing which half, on a screen about who can do what.
      const unsaved = stillPending.map((x) => x.title).join(", ");
      setError(unsaved ? `${detail} — ${t("users.notSaved", { systems: unsaved })}` : detail);
      // Deliberately no refetch: it would overwrite the ticks that have not landed yet,
      // which is the data-loss this screen already had once.
    } finally {
      setBusy(false);
    }
  }


  async function remove() {
    if (!window.confirm(t("users.deleteConfirm", { name: user.full_name }))) return;
    setBusy(true);
    try {
      await api.del(`/users/${user.id}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("users.deleteFailed"));
    } finally {
      setBusy(false);
    }
  }

  function copyId() {
    void navigator.clipboard?.writeText(user.id).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="editor">
      {error && (
        <p className="banner error" role="alert">
          {error}
        </p>
      )}
      {note && (
        <p className="banner ok" role="status">
          <Icon name="check" size={15} />
          {note}
        </p>
      )}
      {warning && (
        <p className="banner warn" role="status">
          {warning}
        </p>
      )}

      {/* --- identity ------------------------------------------------------ */}
      <form onSubmit={saveAll}>
        <div className="row-between">
          <h3>{t("users.identity")}</h3>
          <div className="row-end editor-head-actions">
            {/* The id is a value to copy, not an action in the same family as Save --
                so the code itself is the control and wears no button chrome. */}
            <button type="button" className="btn quiet small" onClick={copyId} title={t("users.copyId")}>
              <code className="faint">{user.id}</code>
              <span>{copied ? t("users.copied") : t("users.uuid")}</span>
            </button>
            {/* Destructive, and therefore nowhere near Save. It used to sit in the same
                row, one control away, at `small` for no reason anybody recorded. */}
            <button
              type="button"
              className="btn danger"
              onClick={() => void remove()}
              disabled={busy}
            >
              {t("users.deleteUser")}
            </button>
          </div>
        </div>

        <div className="grid-2">
          <div>
            <label htmlFor={`n-${user.id}`}>{t("users.fullName")}</label>
            <input
              id={`n-${user.id}`}
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor={`e-${user.id}`}>{t("users.email")}</label>
            <input
              id={`e-${user.id}`}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="muted small">{t("users.emailHelp")}</p>
          </div>
        </div>

        <div className="grid-2">
          <div>
            <label htmlFor={`p-${user.id}`}>{t("users.newPassword")}</label>
            <input
              id={`p-${user.id}`}
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={password}
              placeholder="••••••••••••"
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="muted small">{t("users.newPasswordHelp")}</p>
          </div>
          <div>
            <label htmlFor={`r-${user.id}`}>{t("users.role")}</label>
            <Select
              id={`r-${user.id}`}
              value={role}
              onChange={changeRole}
              ariaLabel={t("users.role")}
              options={roles.map((r) => ({
                value: r.key,
                label: r.name,
                description: r.description,
              }))}
            />
            {selectedRole?.description && (
              <p className="muted small">{selectedRole.description}</p>
            )}
          </div>
        </div>

        <label className="check" style={{ marginBlockStart: "var(--s-4)" }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span>{t("users.active")}</span>
        </label>

        {/* ONE primary action. Cancel genuinely reverts rather than merely closing, and
            Delete has left this row entirely -- it was the only destructive control on the
            screen and it sat a thumb from Save wearing `small` for no reason. */}
        <div className="row-end editor-actions">
          {dirty && (
            <span className="muted small editor-pending">
              {t("users.unsaved", {
                what: [
                  ...(profileChanged ? [t("users.theProfile")] : []),
                  ...pendingSystems.map((x) => x.title),
                ].join(", "),
              })}
            </span>
          )}
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setFullName(user.full_name);
              setEmail(user.email);
              setIsActive(user.is_active);
              setRole(user.roles[0] ?? "");
              setPassword("");
              setHeld(new Set(access?.effective ?? []));
              setError(null);
              setWarning(null);
            }}
            disabled={busy || !dirty}
          >
            {t("users.revert")}
          </button>
          <button type="submit" className="btn primary" disabled={busy || !dirty}>
            {busy ? t("users.saving") : t("users.save")}
          </button>
        </div>
      </form>

      {/* --- what the role fixes ------------------------------------------- */}
      {roleGrants.features.length > 0 && (
        <section className="locked-block">
          <h4>{t("users.features")}</h4>
          <p className="muted small">
            {t("users.featuresHelp", { role: selectedRole?.name ?? role })}
          </p>
          <div className="chips" style={{ marginBlockStart: "var(--s-2)" }}>
            {roleGrants.features.map((k) => (
              <span key={k} className="chip locked-chip" title={t("users.features")}>
                <Icon name="shield" size={12} />
                <code>{k}</code>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* --- adjustable access --------------------------------------------- */}
      <section>
        <h4>{t("users.access")}</h4>
        <p className="muted small">{t("users.accessHelp")}</p>

        <AccessGrid
          held={held}
          onChange={setHeld}
          administers={access?.effective.includes("analysis:admin") ?? false}
          role={role}
          fromRole={fromRole}
        />

        {/* The raw per-permission list that used to sit here -- Revoked / Default / Granted
            for every key -- is gone. It was the first thing an administrator saw on opening
            Edit, and it asks a question nobody was trying to answer: the job is "what can
            this person do", and that is the grid above. The third state it existed to show
            is still REACHABLE, because unticking a box the role grants still sends a revoke;
            what is no longer shown is which of the two an absent tick was. */}
      </section>
    </div>
  );
}
